import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { Alert, Container } from 'react-bootstrap';
import { getFileJobs } from '../../apis/fileApi';
import { dateTimeFormatter } from '../../library/formatters';
import { IdTableCellLink } from '../Table/IdTableCellLink';
import { CustomStateCell } from '../Table/CustomStateCell';
import { SubmittedByCell } from '../Jobs/SubmittedByCell';

const jobColumns = [
    {
        field: 'id',
        headerName: 'Job ID',
        cellRenderer: IdTableCellLink,
        cellRendererParams: { path: '/jobs/' },
        sortable: true,
    },
    { field: 'description', sortable: true, tooltipField: 'description' },
    { field: 'state', cellRenderer: CustomStateCell, sortable: true },
    { field: 'priority', sortable: true },
    { field: 'created', headerName: 'Created', valueFormatter: dateTimeFormatter, sortable: true },
    { field: 'created', headerName: 'Submitted By', cellRenderer: SubmittedByCell, sortable: false },
    { field: 'billCode', headerName: 'Bill Code', sortable: true },
];

export const FileJobsTable = ({ fileId }) => {
    const { data: fileJobs, isLoading, isError } = useQuery({
        queryKey: ['file-jobs', fileId],
        queryFn: () => getFileJobs(fileId),
        enabled: Boolean(fileId),
    });

    if (isLoading) {
        return (
            <Container fluid className="loading-container py-4" aria-live="polite">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading file job details...</span>
                    </div>
                    <h5 className="mt-3">Loading related jobs</h5>
                </div>
            </Container>
        );
    }

    if (isError) {
        return (
            <Container fluid className="py-4">
                <Alert variant="danger" role="alert">An error occurred while fetching the file's jobs.</Alert>
            </Container>
        );
    }

    const jobs = Array.isArray(fileJobs) ? fileJobs : fileJobs?.data;
    const rowData = Array.isArray(jobs) ? jobs : [];

    return (
        <section aria-labelledby="file-jobs-heading" className="mt-4">
            <h4 id="file-jobs-heading">Related jobs</h4>
            {rowData.length === 0 ? (
                <p className="text-muted">No jobs are associated with this file.</p>
            ) : (
                <div className="ag-theme-quartz" style={{ width: '100%' }}>
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={jobColumns}
                        defaultColDef={{
                            flex: 1,
                            minWidth: 120,
                            filter: true,
                            filterParams: { maxNumConditions: 1 },
                        }}
                        pagination
                        paginationPageSize={25}
                        paginationPageSizeSelector={[15, 25, 50, 100]}
                    />
                </div>
            )}
        </section>
    );
};